import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Modal, Space, Spin, Typography, message } from 'antd';
import { CloseOutlined, DownloadOutlined, PrinterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { fetchStorageContainerDetailWithContents, fetchUnifiedContainerDetail, previewOutputContainerLabel } from '../../services/storage';
import StorageLabelPreview from '../admin-staff/storage/labels/components/StorageLabelPreview';
import { storageContainerAdapter } from '../admin-staff/storage/labels/adapters/containerAdapter';
import { storageLabelService } from '../admin-staff/storage/labels/services/storageLabelService';
import { handleLabelError } from '../admin-staff/storage/labels/utils/labelUtils';
import type { StorageContainerLabelData } from '../admin-staff/storage/labels/types/storageLabel';
import { ContainerKind } from '../../types/nonstandardOps';
import { printPdfBlob } from '../../utils/printAgent';

/**
 * 终审生成的 SC / IOC 的容器标签：和仓位表页面的"打印标签"是同一套。
 * SC：查容器详情和内容 → 仓储容器标签数据 → 仓储容器标签预览（打印 / 下载）；
 * IOC：查统一容器详情 → IOC 标签数据 → IOC 标签 PDF 预览（打印 / 下载）；打印优先发给打印代理，代理不可用时用浏览器打印。
 */

/** IOC 标签交给打印代理时的任务类型和尺寸（4×6 英寸）；代理配置里没有这个类型时按尺寸落到 4×6 打印机 */
export const IOC_LABEL_JOB_TYPE = 'OUTPUT_CONTAINER_LABEL';
export const IOC_LABEL_SIZE = { wIn: 4, hIn: 6 };

export interface ContainerLabelTarget {
  kind: ContainerKind;
  id: number;
  code: string;
}

const errorText = (err: any, fallback: string) => err?.response?.data?.error || err?.response?.data?.detail || err?.message || fallback;

/** SC 的标签数据（同仓位表页面）：内容摘要带每个库存单元的编码和数量 */
export const buildScLabelData = async (containerId: number): Promise<StorageContainerLabelData> => {
  const resp = await fetchStorageContainerDetailWithContents(containerId, { c_page_size: 200 });
  const container = resp?.data?.container;
  if (!container) throw new Error('找不到这个仓储容器');
  const rows = Array.isArray(resp?.data?.contents?.results) ? resp.data.contents.results : [];
  const contentItems = rows.map((it: any) => ({ name: String(it.inventory_unit_code || ''), quantity: Number(it.quantity || 0) }));
  const contentSummary = storageContainerAdapter.createContentSummary(contentItems);
  if (contentItems.length > 0) {
    (contentSummary as any).items = contentItems.map((ci: { name: string; quantity: number }) => ({ code: ci.name, quantity: ci.quantity }));
  }
  return storageContainerAdapter.transform(container, {
    contentSummary,
    printOptions: { copies: 1, directPrint: false },
    templateId: storageContainerAdapter.getDefaultTemplateId(container.container_type),
  });
};

/** IOC 标签 PDF（同仓位表页面）：容器编码、指令、项目、类型、尺寸重量、各库存单元数量 */
export const fetchIocLabelPdf = async (containerId: number): Promise<Blob> => {
  const detailResp = await fetchUnifiedContainerDetail('output', containerId, { c_page_size: 500 });
  const detail = detailResp?.data || {};
  const container = detail?.container || {};
  const contentRows = Array.isArray(detail?.contents?.results) ? detail.contents.results : [];
  const details = contentRows
    .map((it: any) => ({ new_code: String(it?.inventory_unit_code || '').trim(), qty: Number(it?.quantity || 0) || 0 }))
    .filter((x: any) => x.new_code && x.qty > 0)
    .sort((a: any, b: any) => (b.qty || 0) - (a.qty || 0));
  const totalQty = details.reduce((sum: number, d: any) => sum + Number(d?.qty || 0), 0);
  const projectName = String(container?.project_name || '').trim();
  const projectSummary = `${String(container?.project_code || '').trim()}${projectName ? ` ${projectName}` : ''}`.trim();
  const l = Number(container?.length_cm);
  const w = Number(container?.width_cm);
  const h = Number(container?.height_cm);
  const g = Number(container?.weight_g);
  const ok = (v: number) => Number.isFinite(v) && v > 0;
  const toIn = (cm: number) => (ok(cm) ? Number((cm / 2.54).toFixed(2)) : '-');
  const toKg = (gg: number) => (ok(gg) ? Number((gg / 1000).toFixed(3)) : '-');
  const toLb = (gg: number) => (ok(gg) ? Number((gg / 453.59237).toFixed(3)) : '-');
  const payload: any = {
    container_code: String(container?.display_code || '').trim(),
    instruction_id: Number(container?.instruction_id || 0) || undefined,
    instruction_code: String(container?.instruction_code || '').trim() || undefined,
    project_summary: projectSummary || undefined,
    container_type_display: String(container?.container_type_display || '').trim() || undefined,
    created_at: container?.created_at ? dayjs(container.created_at).format('YYYY-MM-DD HH:mm:ss') : undefined,
    label_variant: String(container?.content_kind || '').trim().toLowerCase() === 'exception' ? 'exception' : 'normal',
    dims_cm: { l: ok(l) ? Math.round(l) : '-', w: ok(w) ? Math.round(w) : '-', h: ok(h) ? Math.round(h) : '-' },
    dims_in: { l: toIn(l), w: toIn(w), h: toIn(h) },
    weight: { kg: toKg(g), lb: toLb(g) },
    new_kinds: details.length,
    total_qty: totalQty,
    details: details.slice(0, 50),
  };
  const resp = await previewOutputContainerLabel(payload);
  return (resp as any)?.data as Blob;
};

const downloadUrl = (url: string, fileName: string) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * 容器标签打印：openLabel 打开对应的标签预览；在预览里点了"打印"或"下载"（成功）就调用 onPrinted。
 * 返回的 modals 要放进页面里。
 */
export const useContainerLabelPrinter = (onPrinted?: (target: ContainerLabelTarget) => void) => {
  const [loadingId, setLoadingId] = useState<number | null>(null);
  const [scTarget, setScTarget] = useState<ContainerLabelTarget | null>(null);
  const [scData, setScData] = useState<StorageContainerLabelData | undefined>(undefined);
  const [ioc, setIoc] = useState<{ target: ContainerLabelTarget; url: string; blob: Blob } | null>(null);
  const [iocPrinting, setIocPrinting] = useState(false);
  const iocUrlRef = useRef('');
  const onPrintedRef = useRef(onPrinted);
  onPrintedRef.current = onPrinted;

  const releaseIocUrl = () => {
    const url = iocUrlRef.current;
    iocUrlRef.current = '';
    // 稍后再释放：浏览器打印退回到新标签页时，那边可能还在读这个 PDF
    if (url) window.setTimeout(() => { try { URL.revokeObjectURL(url); } catch { /* 已释放 */ } }, 60000);
  };
  useEffect(() => releaseIocUrl, []);

  const openLabel = useCallback(async (target: ContainerLabelTarget) => {
    if (!target.id) { message.error('没有容器 ID，不能生成标签'); return; }
    setLoadingId(target.id);
    try {
      if (target.kind === 'sc') {
        const data = await buildScLabelData(target.id);
        setScTarget(target);
        setScData(data);
      } else {
        const blob = await fetchIocLabelPdf(target.id);
        releaseIocUrl();
        const url = URL.createObjectURL(blob);
        iocUrlRef.current = url;
        setIoc({ target, url, blob });
      }
    } catch (err: any) {
      message.error(`生成标签预览失败：${errorText(err, '未知错误')}`);
    } finally {
      setLoadingId(null);
    }
  }, []);

  const closeIoc = () => { setIoc(null); releaseIocUrl(); };

  /** IOC 打印：用预览时拿到的同一份 PDF，先试打印代理，不可用就用浏览器打印 */
  const printIoc = async () => {
    if (!ioc) return;
    setIocPrinting(true);
    try {
      const result = await printPdfBlob(ioc.blob, {
        jobType: IOC_LABEL_JOB_TYPE,
        labelSize: IOC_LABEL_SIZE,
        fileName: `ioc_label_${ioc.target.code || ioc.target.id}.pdf`,
        code: ioc.target.code,
      });
      if (result.channel === 'agent') {
        message.success(`已发送到打印代理${result.jobId ? `（任务ID：${result.jobId}）` : ''}`);
      } else {
        message.warning(`打印代理不可用（${result.agentError || '无法连接'}），已改用浏览器打印，请在打印对话框里选 4×6 标签打印机`, 6);
      }
      onPrintedRef.current?.(ioc.target);
    } catch (err: any) {
      message.error(`标签打印失败：${errorText(err, '未知错误')}`);
    } finally {
      setIocPrinting(false);
    }
  };

  const modals = (
    <>
      <StorageLabelPreview
        visible={!!scData}
        data={scData}
        onClose={() => { setScData(undefined); setScTarget(null); }}
        onPrint={async (data) => {
          try {
            const result: any = await storageLabelService.printLabel(data);
            if (result?.success) {
              message.success(`标签打印成功！${result.printJobId || result.print_job_id ? `任务ID：${result.printJobId || result.print_job_id}` : ''}`);
              if (scTarget) onPrintedRef.current?.(scTarget);
            } else {
              message.error(`标签打印失败：${result?.message || ''}`);
            }
          } catch (error) {
            message.error(`标签打印失败：${handleLabelError(error, '打印').details}`);
          }
        }}
        onDownload={async (data) => {
          try {
            const blob = await storageLabelService.downloadLabel(data);
            const url = URL.createObjectURL(blob);
            downloadUrl(url, `仓储容器标签_${data?.containerInfo?.systemCode || 'label'}.pdf`);
            URL.revokeObjectURL(url);
            message.success('标签下载成功！');
            if (scTarget) onPrintedRef.current?.(scTarget);
          } catch (error) {
            message.error(`标签下载失败：${handleLabelError(error, '下载').details}`);
          }
        }}
      />
      <Modal
        title={<Space><Typography.Text strong>IOC 标签预览</Typography.Text>{ioc ? <Typography.Text code>{ioc.target.code}</Typography.Text> : null}</Space>}
        open={!!ioc}
        onCancel={closeIoc}
        width={920}
        maskClosable={false}
        destroyOnClose
        footer={[
          <Button key="close" icon={<CloseOutlined />} onClick={closeIoc}>关闭</Button>,
          <Button
            key="download"
            className="nonstd-ioc-label-download"
            icon={<DownloadOutlined />}
            onClick={() => {
              if (!ioc) return;
              downloadUrl(ioc.url, `IOC标签_${ioc.target.code || 'label'}.pdf`);
              onPrintedRef.current?.(ioc.target);
            }}
          >
            下载
          </Button>,
          <Button
            key="print"
            type="primary"
            className="nonstd-ioc-label-print"
            icon={<PrinterOutlined />}
            loading={iocPrinting}
            onClick={() => { void printIoc(); }}
          >
            打印
          </Button>,
        ]}
      >
        {ioc ? (
          <iframe src={ioc.url} title="IOC标签预览" style={{ width: '100%', height: '72vh', border: 'none' }} />
        ) : (
          <div style={{ minHeight: 260, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spin /></div>
        )}
      </Modal>
    </>
  );

  return { openLabel, loadingId, modals };
};
